import React, { useState } from 'react';
import {
  Table, Tag, Button, Space, Modal, Upload, message,
  List, Progress, Card, Switch
} from 'antd';
import {
  UploadOutlined, InboxOutlined, CheckCircleOutlined,
  WarningOutlined, ExclamationCircleOutlined, FileTextOutlined,
  FilePdfOutlined, EyeOutlined, DeleteOutlined
} from '@ant-design/icons';
import axios from 'axios';
import AuthService from '../../utils/auth';
import dayjs from 'dayjs';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3060/api';

const StepTicketUpload = ({ tourists, tour, completeness, onRefresh }) => {
  const [modalVisible, setModalVisible] = useState(false);
  const [files, setFiles] = useState([]);
  const [matchName, setMatchName] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [progress, setProgress] = useState(0);
  const [currentFile, setCurrentFile] = useState('');
  const [completedCount, setCompletedCount] = useState(0);

  const touristDetails = completeness?.tourists || [];
  const stats = completeness?.tour?.touristStats;

  const handleUpload = async () => {
    if (files.length === 0) {
      message.error('请选择要上传的机票文件');
      return;
    }

    setUploading(true);
    setResult(null);
    setProgress(0);
    setCurrentFile('');
    setCompletedCount(0);

    const totalFiles = files.length;
    const results = { success: [], notMatched: [], errors: [] };
    const departureDate = tour?.departureDate ? dayjs(tour.departureDate).format('YYYY-MM-DD') : '';

    try {
      for (let i = 0; i < totalFiles; i++) {
        const file = files[i];
        setCurrentFile(file.name);
        setProgress(Math.round((i / totalFiles) * 100));

        const formData = new FormData();
        formData.append('departureDate', departureDate);
        formData.append('files', file);
        if (!matchName) {
          formData.append('matchMode', 'all');
        }

        try {
          const response = await axios.post(
            `${API_BASE}/tourist-documents/batch-upload-flights`,
            formData,
            {
              headers: {
                ...AuthService.getAuthHeaders(),
                'Content-Type': 'multipart/form-data'
              }
            }
          );

          if (response.data.success) {
            const data = response.data.data;
            if (data.success) results.success.push(...data.success);
            if (data.notMatched) results.notMatched.push(...data.notMatched);
            if (data.errors) results.errors.push(...data.errors);
          }
        } catch (err) {
          results.errors.push({
            fileName: file.name,
            error: err.response?.data?.error || '上传失败'
          });
        }

        setCompletedCount(i + 1);
      }

      setProgress(100);
      setResult(results);

      const successCount = results.success.length;
      const notMatchedCount = results.notMatched.length;
      const errorCount = results.errors.length;
      message.success(`上传完成: ${successCount} 成功, ${notMatchedCount} 未匹配, ${errorCount} 失败`);

      onRefresh();
    } catch (error) {
      message.error(error.response?.data?.error || '批量上传失败');
    } finally {
      setUploading(false);
      setProgress(0);
      setCurrentFile('');
      setCompletedCount(0);
    }
  };

  const handleFileChange = (info) => {
    const fileList = info.fileList
      .filter(file => file.originFileObj && file.type === 'application/pdf')
      .map(file => file.originFileObj);
    setFiles(fileList);
  };

  const handleUploadSingle = async (touristId, file) => {
    const formData = new FormData();
    formData.append('document', file);
    formData.append('documentType', 'flight');
    formData.append('documentName', file.name);

    try {
      await axios.post(
        `${API_BASE}/tourist-documents/tourist/${touristId}`,
        formData,
        {
          headers: {
            ...AuthService.getAuthHeaders(),
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      message.success('上传成功');
      onRefresh();
    } catch (err) {
      message.error('上传失败: ' + (err.response?.data?.error || err.message));
    }
    return false;
  };

  const handleViewTicket = async (documentId) => {
    try {
      const res = await axios.get(`${API_BASE}/tourist-documents/${documentId}/url`, {
        headers: AuthService.getAuthHeaders()
      });
      if (res.data.success && res.data.data?.fileUrl) {
        window.open(res.data.data.fileUrl, '_blank');
      } else {
        message.error('获取文件地址失败');
      }
    } catch (err) {
      message.error('获取文件地址失败');
    }
  };

  const handleDeleteTicket = async (docId) => {
    try {
      await axios.delete(`${API_BASE}/tourist-documents/${docId}`, {
        headers: AuthService.getAuthHeaders()
      });
      message.success('已删除');
      onRefresh();
    } catch (err) {
      message.error('删除失败');
    }
  };

  const handleModalClose = () => {
    setModalVisible(false);
    setFiles([]);
    setResult(null);
    setMatchName(true);
    onRefresh();
  };

  const columns = [
    {
      title: '护照姓名',
      dataIndex: 'passportName',
      key: 'passportName',
      width: 180,
      render: (val) => val || '-',
    },
    {
      title: '护照号',
      dataIndex: 'passportNumber',
      key: 'passportNumber',
      width: 120,
      render: (val) => val || '-',
    },
    {
      title: '机票PDF',
      key: 'flightTicket',
      width: 240,
      render: (_, record) => {
        const docs = record.flightDocuments || [];
        return (
          <div>
            {docs.length > 0 ? (
              <div style={{ marginBottom: 4 }}>
                {docs.map((doc) => (
                  <div key={doc.id} style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 2, fontSize: 12 }}>
                    <FilePdfOutlined style={{ color: '#ff4d4f', flexShrink: 0 }} />
                    <span
                      style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#1890ff', cursor: 'pointer', maxWidth: 140 }}
                      title={doc.documentName}
                      onClick={() => handleViewTicket(doc.id)}
                    >
                      {doc.documentName}
                    </span>
                    <Button type="link" size="small" icon={<EyeOutlined />} style={{ padding: 0, height: 'auto', minWidth: 0 }} onClick={() => handleViewTicket(doc.id)} />
                    <Button type="link" size="small" danger icon={<DeleteOutlined />} style={{ padding: 0, height: 'auto', minWidth: 0 }} onClick={() => handleDeleteTicket(doc.id)} />
                  </div>
                ))}
              </div>
            ) : (
              <Tag color="default" style={{ marginBottom: 4 }}>未上传</Tag>
            )}
            <Upload
              beforeUpload={(file) => handleUploadSingle(record.id, file)}
              showUploadList={false}
              accept=".pdf"
              multiple
            >
              <Button size="small" icon={<UploadOutlined />} type={docs.length === 0 ? 'primary' : 'default'}>
                {docs.length === 0 ? '上传' : '追加'}
              </Button>
            </Upload>
          </div>
        );
      },
    },
    {
      title: '票号',
      dataIndex: 'ticketNumber',
      key: 'ticketNumber',
      width: 160,
      render: (val) => val || '-',
    },
  ];

  return (
    <div>
      <div className="stat-cards">
        <div className={`stat-card ${stats?.flightTicket === stats?.total ? 'success' : 'warning'}`}>
          <div className="stat-value">{stats?.flightTicket || 0}/{stats?.total || 0}</div>
          <div className="stat-label">已上传机票</div>
        </div>
      </div>

      <div style={{ marginBottom: 16 }}>
        <Button
          type="primary"
          icon={<FileTextOutlined />}
          onClick={() => setModalVisible(true)}
        >
          批量匹配机票
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={touristDetails}
        rowKey="id"
        size="small"
        pagination={false}
      />

      <Modal
        title="批量匹配机票"
        open={modalVisible}
        onCancel={handleModalClose}
        footer={null}
        width={640}
        destroyOnClose
      >
        {uploading ? (
          <div style={{ textAlign: 'center', padding: '24px 0' }}>
            <div style={{ marginBottom: 8, fontSize: 16, fontWeight: 500 }}>
              正在上传机票文件 ({completedCount}/{files.length})
            </div>
            <Progress
              percent={progress}
              status="active"
              strokeColor={{ '0%': '#108ee9', '100%': '#87d068' }}
              style={{ maxWidth: 400, margin: '0 auto' }}
            />
            {currentFile && (
              <div style={{ marginTop: 12, color: '#1890ff', fontFamily: 'monospace', fontSize: 13 }}>
                {currentFile}
              </div>
            )}
            <div style={{ marginTop: 12, color: '#999', fontSize: 12 }}>
              请勿关闭此窗口，正在逐个上传并匹配游客...
            </div>
          </div>
        ) : (
          <div>
            <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
              <span>自动匹配：</span>
              <Switch checked={matchName} onChange={setMatchName} />
              <span style={{ color: '#999', fontSize: 12 }}>
                {matchName ? '通过 OCR 识别护照号、姓名、票号自动匹配游客' : '不匹配，直接发给所有游客'}
              </span>
            </div>

            <div style={{ marginBottom: 16 }}>
              <Upload.Dragger
                accept=".pdf"
                multiple
                beforeUpload={() => false}
                onChange={handleFileChange}
                fileList={files.map((file, index) => ({
                  uid: index,
                  name: file.name,
                  status: 'done',
                  originFileObj: file,
                  type: file.type
                }))}
              >
                <p className="ant-upload-drag-icon">
                  <InboxOutlined />
                </p>
                <p className="ant-upload-text">点击或拖拽 PDF 机票到此区域</p>
                <p className="ant-upload-hint">
                  支持多文件上传{matchName ? '，OCR 自动识别姓名/护照号/票号并匹配游客（含多人共票）' : '，将发给所有游客'}
                </p>
              </Upload.Dragger>
            </div>

            {files.length > 0 && (
              <div style={{ color: '#1890ff', marginBottom: 16 }}>
                已选择 {files.length} 个文件
              </div>
            )}

            <div style={{ textAlign: 'right', marginBottom: result ? 16 : 0 }}>
              <Space>
                <Button onClick={handleModalClose}>关闭</Button>
                <Button
                  type="primary"
                  icon={<UploadOutlined />}
                  onClick={handleUpload}
                  disabled={files.length === 0}
                >
                  开始上传
                </Button>
              </Space>
            </div>

            {result && (
              <div>
                <div style={{ marginBottom: 12 }}>
                  <Space size="large">
                    <Tag color="success" style={{ padding: '4px 12px', fontSize: 14 }}>
                      <CheckCircleOutlined /> 成功: {result.success?.length || 0}
                    </Tag>
                    <Tag color="warning" style={{ padding: '4px 12px', fontSize: 14 }}>
                      <WarningOutlined /> 未匹配: {result.notMatched?.length || 0}
                    </Tag>
                    <Tag color="error" style={{ padding: '4px 12px', fontSize: 14 }}>
                      <ExclamationCircleOutlined /> 失败: {result.errors?.length || 0}
                    </Tag>
                  </Space>
                </div>

                {result.success?.length > 0 && (
                  <Card size="small" title="上传成功" style={{ marginBottom: 8 }}>
                    <List
                      size="small"
                      dataSource={result.success}
                      renderItem={(item) => (
                        <List.Item>
                          <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 8 }} />
                          <span>{item.fileName}</span>
                          <span style={{ marginLeft: 8, color: '#666' }}>&rarr; {item.touristName}</span>
                          {item.extractedPassport && (
                            <span style={{ marginLeft: 8, color: '#999', fontSize: 12 }}>
                              (护照: {item.extractedPassport})
                            </span>
                          )}
                        </List.Item>
                      )}
                      style={{ maxHeight: 150, overflow: 'auto' }}
                    />
                  </Card>
                )}

                {result.notMatched?.length > 0 && (
                  <Card size="small" title="未匹配游客" style={{ marginBottom: 8 }}>
                    <List
                      size="small"
                      dataSource={result.notMatched}
                      renderItem={(item) => (
                        <List.Item>
                          <WarningOutlined style={{ color: '#faad14', marginRight: 8 }} />
                          <span>{item.fileName}</span>
                          <span style={{ marginLeft: 8, color: '#999' }}>
                            {item.extractedName && `(识别: ${item.extractedName})`}
                            {item.extractedPassport && ` 护照: ${item.extractedPassport}`}
                            {' '}{item.reason}
                          </span>
                        </List.Item>
                      )}
                      style={{ maxHeight: 120, overflow: 'auto' }}
                    />
                  </Card>
                )}

                {result.errors?.length > 0 && (
                  <Card size="small" title="上传失败" style={{ marginBottom: 8 }}>
                    <List
                      size="small"
                      dataSource={result.errors}
                      renderItem={(item) => (
                        <List.Item>
                          <ExclamationCircleOutlined style={{ color: '#ff4d4f', marginRight: 8 }} />
                          <span>{item.fileName}</span>
                          <span style={{ marginLeft: 8, color: '#ff4d4f' }}>{item.error}</span>
                        </List.Item>
                      )}
                      style={{ maxHeight: 100, overflow: 'auto' }}
                    />
                  </Card>
                )}
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default StepTicketUpload;
